import React, { useState, useEffect } from 'react';
import { Input, Button, Spin, Table, Checkbox } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import { closeSaleAction } from '../store/customerActions';
import '../../Pledge/PaymentModal/index.scss';

interface Jewel {
  _id: string;
  itemType: string;
  purity: string;
  grossWeight: number;
  unwantedWeight: number;
  netWeight: number;
  quantity: number;
  isPledged: boolean;
  pledgeNumber?: string;
  salePrice?: number;
}

interface Sale {
  _id: string;
  saleNumber: string;
  customer: string;
  principal: number;
  jewels: Jewel[];
  branch: any;
  [key: string]: any;
}

interface Props {
  sale: Sale;
  closeModal: () => void;
  refetchCustomer: (customerId: string) => void;
}

export default function CloseModal({
  sale,
  closeModal,
  refetchCustomer,
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jewelPrices, setJewelPrices] = useState<Record<string, number>>({});
  const [selectedJewels, setSelectedJewels] = useState<Record<string, boolean>>(
    {}
  );
  const [onlineAmount, setOnlineAmount] = useState<number>(0);
  const [offlineAmount, setOfflineAmount] = useState<number>(0);
  const [invalidAmounts, setInvalidAmounts] = useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();

  const resetForm = () => {
    setJewelPrices({});
    setSelectedJewels({});
    setOnlineAmount(0);
    setOfflineAmount(0);
    setInvalidAmounts(false);
  };

  // Reset form when modal opens with a new sale
  useEffect(() => {
    resetForm();
  }, [sale.saleNumber]);

  const handlePriceChange = (jewelId: string, value: number) => {
    setJewelPrices((prevState) => ({
      ...prevState,
      [jewelId]: value,
    }));
  };

  const handleCheckboxChange = (jewelId: string, checked: boolean) => {
    setSelectedJewels((prevState) => ({
      ...prevState,
      [jewelId]: checked,
    }));
  };

  const jewelColumns = [
    {
      title: 'Select',
      dataIndex: 'select',
      key: 'select',
      width: 80,
      render: (_: any, record: Jewel) => (
        <Checkbox
          disabled={
            record.isPledged ||
            (record.salePrice !== undefined && record.salePrice > 0)
          }
          checked={selectedJewels[record._id] ?? false}
          onChange={(e) => {
            handleCheckboxChange(record._id, e.target.checked);
          }}
        />
      ),
    },
    {
      title: 'Item Type',
      dataIndex: 'itemType',
      key: 'itemType',
      width: 150,
      render: (_: any, record: Jewel) => <p>{record.itemType}</p>,
    },
    {
      title: 'Purity',
      dataIndex: 'purity',
      key: 'purity',
      width: 100,
      render: (_: any, record: Jewel) => <p>{record.purity}</p>,
    },
    {
      title: 'Net Weight (g)',
      dataIndex: 'netWeight',
      key: 'netWeight',
      width: 120,
      render: (_: any, record: Jewel) => <p>{record.netWeight}</p>,
    },
    {
      title: 'Pledged',
      dataIndex: 'isPledged',
      key: 'isPledged',
      width: 100,
      render: (_: any, record: Jewel) => (
        <p>{record.isPledged ? 'Yes' : 'No'}</p>
      ),
    },
    {
      title: 'Sale Price',
      dataIndex: 'salePrice',
      key: 'salePrice',
      width: 180,
      render: (_: any, record: Jewel) => {
        const existingPrice = record.salePrice ?? 0;
        return (
          <Input
            type="number"
            disabled={
              record.isPledged ||
              (record.salePrice !== undefined && record.salePrice > 0)
            }
            value={jewelPrices[record._id] ?? existingPrice}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handlePriceChange(record._id, Number(e.target.value));
            }}
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            placeholder={record.isPledged ? 'Pledged' : 'Enter price'}
            className="payment__input"
          />
        );
      },
    },
  ];

  const handleCloseSale = async () => {
    setIsLoading(true);

    // Build jewel updates array - only include selected jewels with sale price > 0
    const jewelUpdates = sale.jewels
      .filter((jewel) => {
        const isSelected = selectedJewels[jewel._id] ?? false;
        const price = jewelPrices[jewel._id] ?? jewel.salePrice ?? 0;
        return isSelected && price > 0;
      })
      .map((jewel) => ({
        jewelId: jewel._id,
        salePrice: jewelPrices[jewel._id] ?? jewel.salePrice ?? 0,
      }));

    // Calculate total amount from selected jewel prices
    const totalAmount = jewelUpdates.reduce(
      (sum, jewel) => sum + jewel.salePrice,
      0
    );

    const payload = {
      saleNumber: sale.saleNumber,
      jewelUpdates,
      onlineAmount,
      offlineAmount,
      totalAmount,
      branch: sale.branch._id,
    };

    await closeSaleAction(dispatch, payload);
    setIsLoading(false);
    resetForm();
    refetchCustomer(sale.customer);
    closeModal();
  };

  // Check if all selected jewels have a sale price > 0
  const allSelectedPricesEntered = sale.jewels.every((jewel) => {
    const isSelected = selectedJewels[jewel._id] ?? false;
    if (!isSelected) return true; // Not selected, so skip validation
    const currentPrice = jewelPrices[jewel._id] ?? jewel.salePrice ?? 0;
    return currentPrice > 0;
  });

  // Check if at least one jewel is selected
  const hasSelectedJewels = Object.values(selectedJewels).some(
    (selected) => selected
  );

  // Calculate total amount from selected jewel prices only
  const totalAmount = sale.jewels.reduce((sum, jewel) => {
    const isSelected = selectedJewels[jewel._id] ?? false;
    if (!isSelected) return sum;
    const price = jewelPrices[jewel._id] ?? jewel.salePrice ?? 0;
    return sum + price;
  }, 0);

  // Validate online + offline = total amount
  const amountsValid =
    Number(onlineAmount) + Number(offlineAmount) === Number(totalAmount);

  return (
    <div className="pledge__payment__modal">
      <div className="divider" />
      <h2>Close Sale</h2>
      <p style={{ marginBottom: 20, color: '#CF0F0F' }}>
        Select jewels to sell and enter their sale prices. Only selected jewels
        with sale price greater than zero will be processed. Pledged jewels
        cannot be selected.
      </p>
      <div style={{ width: '100%', marginBottom: 24 }}>
        <Table
          dataSource={sale.jewels}
          columns={jewelColumns}
          pagination={false}
          rowKey={(record) => record._id}
        />
      </div>

      <div className="payment__form">
        <div>
          <h4>Total Amount (from selected jewels)</h4>
          <Input
            type="number"
            disabled
            value={totalAmount}
            className="payment__input"
          />
        </div>
        <div>
          <h4>Online Amount</h4>
          <Input
            type="number"
            value={onlineAmount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOnlineAmount(Number(e.target.value));
              setInvalidAmounts(
                Number(e.target.value) + Number(offlineAmount) !== totalAmount
              );
            }}
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            className="payment__input"
            placeholder="Enter online amount"
          />
          {invalidAmounts && (
            <p
              className="error__message"
              style={{ color: '#DD1E2D', marginTop: 8 }}
            >
              Online + Offline must equal Total Amount
            </p>
          )}
        </div>
        <div>
          <h4>Offline Amount</h4>
          <Input
            type="number"
            value={offlineAmount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOfflineAmount(Number(e.target.value));
              setInvalidAmounts(
                Number(onlineAmount) + Number(e.target.value) !== totalAmount
              );
            }}
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            className="payment__input"
            placeholder="Enter offline amount"
          />
        </div>
      </div>

      <div className="payment__actions__section">
        <Button
          onClick={() => {
            resetForm();
            closeModal();
          }}
          className="payment__cancel__button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleCloseSale}
          disabled={
            !hasSelectedJewels ||
            !allSelectedPricesEntered ||
            !amountsValid ||
            isLoading
          }
          className="payment__save__button"
        >
          {isLoading ? (
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 24, color: 'white' }}
                  spin
                />
              }
            />
          ) : (
            <span>Close Sale</span>
          )}
        </Button>
      </div>
    </div>
  );
}
